import { alpha, Theme } from '@mui/material/styles';
import { intervalToDuration } from 'date-fns';
import { getTrans } from 'modules/i18n';
import { GeneralStatus } from 'modules/status-badge';
import { ReasonTimeMismatchType } from './types';

export const autoCloseLabelMap: Record<GeneralStatus, () => string> = {
  OPEN: () => getTrans('weekDetailsPage.autoCloseLabel.open'),
  CLOSED: () => getTrans('weekDetailsPage.autoCloseLabel.closed'),
  CONFIRMED: () => getTrans('weekDetailsPage.autoCloseLabel.closed'),
  CORRECTED: () => getTrans('weekDetailsPage.autoCloseLabel.closed'),
  IN_REVIEW: () => getTrans('weekDetailsPage.autoCloseLabel.closed'),
  OPERATOR_REVIEWING_TALENT_DATA: () =>
    getTrans('weekDetailsPage.autoCloseLabel.closed'),
  REPORTED: () => ''
};

export const mapReasonTimeMismatchType: Record<
  ReasonTimeMismatchType,
  () => string
> = {
  [ReasonTimeMismatchType.COMPANY_DECISION]: () =>
    getTrans('reason.type.companyDecision'),
  [ReasonTimeMismatchType.TALENT_DECISION]: () =>
    getTrans('reason.type.talentDecision'),
  [ReasonTimeMismatchType.TIME_OFF]: () => getTrans('reason.type.timeOff'),
  [ReasonTimeMismatchType.COMMENT]: () => getTrans('reason.type.comment'),
  [ReasonTimeMismatchType.COMPENSATION_MORE_HOURS]: () =>
    getTrans('reason.type.compensationMoreHours'),
  [ReasonTimeMismatchType.COMPENSATION_LESS_HOURS]: () =>
    getTrans('reason.type.compensationLessHours'),
  [ReasonTimeMismatchType.TOO_MANY_PERIOD_HOURS]: () =>
    getTrans('reason.type.tooManyPeriodHours'),
  [ReasonTimeMismatchType.TOO_MANY_SHIFTS]: () =>
    getTrans('reason.type.tooManyShifts'),
  [ReasonTimeMismatchType.SHIFT_START_TIME]: () =>
    getTrans('reason.type.shiftStartTime'),
  [ReasonTimeMismatchType.SHIFT_END_TIME]: () =>
    getTrans('reason.type.shiftEndTime'),
  [ReasonTimeMismatchType.BREAK_START_TIME]: () =>
    getTrans('reason.type.breakStartTime'),
  [ReasonTimeMismatchType.BREAK_END_TIME]: () =>
    getTrans('reason.type.breakEndTime'),
  [ReasonTimeMismatchType.TOO_LONG_SHIFT_DURATION]: () =>
    getTrans('reason.type.tooLongShiftDuration')
};

export const getDurationLabel = (autoCloseDuration: Duration) => {
  const { days, hours, minutes } = autoCloseDuration;
  const actualValues: {
    d: number | undefined;
    h: number | undefined;
    min: number | undefined;
  } = {
    d: days,
    h: hours,
    min: minutes
  };

  let label = '';

  Object.entries(actualValues).forEach(([key, value]) => {
    if (value) {
      label += `${value}${key} `;
    }
    if (!value) {
      if (label || key === 'min') {
        label += `0${key} `;
      }
    }
  });

  return label.trim();
};

export const getAutoCloseLabel = (
  weekStatus: GeneralStatus,
  weekAutoClosing: string,
  now = new Date()
): string => {
  if (
    weekStatus === GeneralStatus.CLOSED ||
    weekStatus === GeneralStatus.OPERATOR_REVIEWING_TALENT_DATA
  ) {
    return autoCloseLabelMap[weekStatus]();
  }

  if (!weekAutoClosing || new Date(weekAutoClosing) < now) {
    return '';
  }

  const autoCloseDuration = intervalToDuration({
    start: now,
    end: new Date(weekAutoClosing)
  });

  return autoCloseLabelMap[weekStatus]
    ? `${autoCloseLabelMap[weekStatus]()}: ${getDurationLabel(
        autoCloseDuration
      )}`
    : '';
};

export const getCompletedPercentage = (
  progressMinutes: number,
  totalMinutes: number
): number => {
  if (totalMinutes <= 0) return 0;

  return (progressMinutes / totalMinutes) * 100;
};

export const getProgressBarBaseColor = (
  theme: Theme,
  expectedMinutes: number,
  reportedMinutes: number
): string => {
  let colorName: string = alpha(theme.palette.success.main, 0.24);

  if (reportedMinutes > expectedMinutes) {
    colorName = theme.palette.warning.main;
  }

  return colorName;
};

export const isSumOfMinutesEqualToTotal = (
  minutes1: number,
  minutes2: number,
  total: number
) => minutes1 + minutes2 === total;

export const isMaxShiftsReached = (shiftsCount: number) => shiftsCount >= 6;

export const isEffectiveValueVisible = (
  effectiveValue: number | string | undefined,
  reportedValue: number | string | undefined,
  status: GeneralStatus | undefined
): boolean => {
  return (
    status === GeneralStatus.CORRECTED &&
    (typeof effectiveValue === 'number' ||
      typeof effectiveValue === 'string') &&
    effectiveValue !== reportedValue
  );
};
